/* ============================================================
   ProviderMaterialStockPanel — MS-1 (TASK-52194f26): opt-in
   «Склад» tab of the performer cabinet. Artem: «исполнителю
   вкладку для отслеживания остатков материалов разных цветов».

   Manual tracking rows (material + color + remaining grams +
   low-stock threshold); save = full replace on the backend.
   Colors with remaining > 0 show up in the customer color
   picker at performer selection — the intro card says so.

   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md
   (no gradients, no heavy shadows).
   ============================================================ */

import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { MaterialStockItem, MaterialStockRow } from '../types/provider';
import type { Material } from '../types';
import {
  EMPTY_DRAFT,
  type StockDraft,
  draftToItem,
  formatGrams,
  hasDuplicateColors,
  inStockColorCount,
  isLowStock,
} from './materialStockView';
import { fallbackHexFor, swatchDotStyle } from './colorModeView';

export function ProviderMaterialStockPanel() {
  const [rows, setRows] = useState<MaterialStockRow[] | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [draft, setDraft] = useState<StockDraft>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [stock, mats] = await Promise.all([api.getProviderMaterialStock(), api.getMaterials()]);
      setRows(stock);
      setMaterials(mats.filter((m) => m.isActive));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить склад');
      setRows([]);
    }
  };

  useEffect(() => { load(); }, []);

  const items = useMemo<MaterialStockItem[]>(
    () => (rows ?? []).map((r) => ({
      materialId: r.materialId,
      color: r.color,
      hexColor: r.hexColor ?? null,
      remainingGrams: r.remainingGrams,
      lowStockThresholdGrams: r.lowStockThresholdGrams,
    })),
    [rows],
  );

  const materialName = (id: string) =>
    materials.find((m) => m.id === id)?.name ?? '—';

  const handleAdd = () => {
    const parsed = draftToItem(draft);
    if ('error' in parsed) {
      setDraftError(parsed.error);
      return;
    }
    const next = [...items, parsed.item];
    if (hasDuplicateColors(next)) {
      setDraftError('Такой цвет для этого материала уже есть в списке');
      return;
    }
    setDraftError(null);
    setRows(
      next.map((item, i) => ({
        stockId: rows?.[i]?.stockId ?? `draft-${i}`,
        materialId: item.materialId,
        materialName: materialName(item.materialId),
        materialType: 0,
        color: item.color,
        hexColor: item.hexColor ?? null,
        remainingGrams: item.remainingGrams,
        lowStockThresholdGrams: item.lowStockThresholdGrams,
        isLowStock: isLowStock(item),
      })),
    );
    setDraft(EMPTY_DRAFT);
  };

  const handleRemove = (index: number) => {
    setRows((prev) => (prev ?? []).filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (hasDuplicateColors(items)) {
      setError('Один цвет указан дважды для одного материала');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await api.saveProviderMaterialStock(items);
      setRows(saved);
      setSavedAt(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить склад');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', margin: 0 }}>
          Склад материалов
        </p>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Отмечайте, что осталось по цветам — покупатель увидит доступные цвета при выборе исполнителя.
          {' '}В наличии: {inStockColorCount(rows ?? [])} цвет(ов).
        </p>
      </div>

      {error && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', margin: 0 }}>{error}</p>
      )}

      {rows === null ? (
        <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-lg)' }}>
          <div className="spinner" />
        </div>
      ) : (
        <div className="card" style={{ padding: 'var(--space-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {rows.length === 0 ? (
            <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', margin: 0 }}>
              Пока пусто. Добавьте строки — материал, цвет и сколько граммов осталось.
            </p>
          ) : (
            rows.map((row, index) => (
              <div
                key={row.stockId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  padding: 'var(--space-xs) 0',
                  borderBottom: index < rows.length - 1 ? '1px solid var(--color-separator)' : 'none',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* ac930ee2: dot shows the hand-tuned hex, else the name-resolved
                      one (mirrors what customers will see); title says which. */}
                  <span
                    style={swatchDotStyle(row.hexColor ?? fallbackHexFor(row.color))}
                    title={row.hexColor ?? (fallbackHexFor(row.color) != null ? 'hex подобран по названию' : undefined)}
                  />
                  <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}>
                    {row.materialName}
                  </span>
                  <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginLeft: 'var(--space-xs)' }}>
                    · {row.color}
                  </span>
                  {row.isLowStock && (
                    <span
                      className="badge"
                      style={{
                        marginLeft: 'var(--space-xs)',
                        fontSize: 'var(--fs-caption)',
                        color: 'var(--color-warning, #b25000)',
                        background: 'rgba(255,149,0,0.10)',
                        borderRadius: 'var(--radius-pill)',
                        padding: '1px 8px',
                      }}
                    >
                      мало
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                  {formatGrams(row.remainingGrams)}
                  {row.lowStockThresholdGrams > 0 ? ` (порог ${formatGrams(row.lowStockThresholdGrams)})` : ''}
                </span>
                <button
                  type="button"
                  className="btn btn-tertiary"
                  style={{ height: 26, padding: '2px 10px', fontSize: 'var(--fs-caption)' }}
                  onClick={() => handleRemove(index)}
                >
                  Убрать
                </button>
              </div>
            ))
          )}

          {/* Add-row form */}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Материал</span>
              <select
                className="input"
                style={{ minWidth: 140 }}
                value={draft.materialId}
                onChange={(e) => setDraft({ ...draft, materialId: e.target.value })}
              >
                <option value="">— выбрать —</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Цвет</span>
              <input
                className="input"
                style={{ minWidth: 120, width: 140 }}
                placeholder="Чёрный"
                value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
              />
            </label>
            {/* ecfe2257: hex swatch for the customer chips — native picker + hex preview.
                ac930ee2: with no hand-tuned hex the dot previews the name-resolved
                color (what customers see when the row is left without a hex). */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Образец</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38 }}>
                <span
                  style={swatchDotStyle(draft.hexColor || fallbackHexFor(draft.color) || undefined)}
                  title={draft.hexColor.trim() || (fallbackHexFor(draft.color) != null ? 'подберётся по названию' : undefined)}
                />
                <input
                  type="color"
                  aria-label="Hex цвета"
                  style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--color-separator)', borderRadius: 'var(--radius-sm, 6px)', background: 'var(--color-surface)' }}
                  value={/^#[0-9A-Fa-f]{6}$/.test(draft.hexColor.trim()) ? draft.hexColor.trim() : '#000000'}
                  onChange={(e) => setDraft({ ...draft, hexColor: e.target.value.toUpperCase() })}
                />
                <button
                  type="button"
                  className="btn btn-tertiary"
                  style={{ height: 26, padding: '2px 8px', fontSize: 'var(--fs-caption)' }}
                  onClick={() => setDraft({ ...draft, hexColor: '' })}
                >
                  Сброс
                </button>
              </span>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Остаток, г</span>
              <input
                className="input"
                style={{ width: 110 }}
                inputMode="decimal"
                placeholder="850"
                value={draft.remainingGrams}
                onChange={(e) => setDraft({ ...draft, remainingGrams: e.target.value })}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Порог «мало», г</span>
              <input
                className="input"
                style={{ width: 110 }}
                inputMode="decimal"
                placeholder="200"
                value={draft.lowStockThresholdGrams}
                onChange={(e) => setDraft({ ...draft, lowStockThresholdGrams: e.target.value })}
              />
            </label>
            <button type="button" className="btn btn-secondary" onClick={handleAdd}>
              Добавить
            </button>
          </div>
          {draftError && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', margin: 0 }}>{draftError}</p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Сохраняем…' : 'Сохранить склад'}
            </button>
            {savedAt && !saving && (
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                Сохранено в {savedAt}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
